import * as React from "react"
import { ThemeCss, Button, Spacer } from "gatsby-interface"
import Loading from "@modules/ui/components/Loading"
import {
  importSite as importSiteText,
  ui as uiText,
} from "@modules/locales/default.js"
import { useCmsIntegrationsForSiteQuery } from "@modules/cms/shared/queries.generated"
import { useImportedSiteDetailsQuery } from "@modules/site/create/Import/queries.generated"
import { usePublishSite } from "@modules/site/create/shared/hooks/usePublishSite"
import { WizardStepHeader } from "@modules/ui/components/WizardStepHeader"
import { FormattedMessage } from "@modules/locales"
import { WizardFooter } from "@modules/ui/components/WizardFooter"
import { SiteInformation } from "./SiteInformation"
import { NarrowSingleColumn } from "@modules/ui/layouts/Containers"
import { MdArrowForward } from "react-icons/md"
import { ErrorAlert } from "@modules/ui/components/ErrorAlert"
import { useFlags } from "@modules/featureFlags"

const repoNameCss: ThemeCss = theme => ({
  color: theme.colors.gatsby,
})

export type SetupSiteStepProps = {
  organizationId: string
  siteId: string
  onPublished: () => void
  onGoBack: () => void
}

export function SetupSiteStep({
  organizationId,
  siteId,
  onPublished,
  onGoBack,
}: SetupSiteStepProps) {
  const { flags } = useFlags()
  const [disableCreateSite, setDisableCreateSite] = React.useState<boolean>(
    false
  )
  const {
    data: siteDetailsData,
    loading: siteDetailsLoading,
    error: siteDetailsError,
  } = useImportedSiteDetailsQuery({
    variables: { id: siteId },
    fetchPolicy: `cache-and-network`,
  })
  const {
    data: siteIntegrationsData,
    loading: siteIntegrationsLoading,
    error: siteIntegrationsError,
  } = useCmsIntegrationsForSiteQuery({
    variables: {
      siteId,
      connected: true,
    },
  })

  const [
    publishSite,
    { loading: isPubishing, errorAlert: publishError },
  ] = usePublishSite(siteId)

  if (siteDetailsError || siteIntegrationsError) {
    return (
      <NarrowSingleColumn>
        {siteDetailsError && (
          <ErrorAlert>{siteDetailsError.message}</ErrorAlert>
        )}
        {siteIntegrationsError && (
          <ErrorAlert>{siteIntegrationsError.message}</ErrorAlert>
        )}
      </NarrowSingleColumn>
    )
  }

  if (siteDetailsLoading || siteIntegrationsLoading) {
    return (
      <Loading
        delay={1000}
        message={importSiteText.messages.loadingDataForSetup}
        bufferSize="padded"
      />
    )
  }

  const repoName = siteDetailsData?.siteDetails?.repository?.nameWithOwner || ""

  return (
    <React.Fragment>
      <NarrowSingleColumn>
        <WizardStepHeader
          title={
            <FormattedMessage<"repoName", "strong">
              message={importSiteText.headers.setupSiteStep}
              values={{
                repoName,
              }}
              tags={{
                strong: function(content) {
                  return <span css={repoNameCss}>{content}</span>
                },
              }}
            />
          }
        />

        <SiteInformation
          siteId={siteId}
          organizationId={organizationId}
          siteRepositoryNameWithOwner={repoName}
          branch={siteDetailsData?.siteDetails?.branch || ""}
          cmsIntegrationsForSite={
            siteIntegrationsData?.cmsIntegrationsForSite || []
          }
          setDisableCreateSite={setDisableCreateSite}
        />

        {publishError && (
          <React.Fragment>
            <Spacer size={5} />
            {publishError}
          </React.Fragment>
        )}
      </NarrowSingleColumn>

      <WizardFooter
        goBackButton={
          <Button variant="SECONDARY" onClick={() => onGoBack()}>
            {uiText.actions.back}
          </Button>
        }
        goNextButton={
          <Button
            data-ld="create-site"
            onClick={() => publishSite(onPublished)}
            loading={isPubishing}
            rightIcon={<MdArrowForward />}
            disabled={flags.deployNowProvisioning && disableCreateSite}
          >
            {importSiteText.actions.publishSite}
          </Button>
        }
      />
    </React.Fragment>
  )
}
